import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// Shared easing for the whole DataPulse Web page. One curve everywhere keeps
// the motion feeling like a single system rather than scattered effects.
export const EASE = [0.22, 1, 0.36, 1];

/**
 * The page's single scroll-reveal primitive. Every section uses this so reveals
 * are consistent in distance, duration and curve. Honours prefers-reduced-motion
 * by rendering the content statically.
 */
export function Reveal({ children, delay = 0, y = 18, className, style }) {
  const reduce = useReducedMotion();

  if (reduce) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      style={style}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.7, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Type that rises from behind its own edge. Used on section headings so the
 * display face gets a moment of craft rather than a plain fade.
 *
 * The wrapper adds room below the baseline (and pulls it back with a negative
 * margin) so descenders aren't clipped by the mask.
 */
export function MaskReveal({ children, delay = 0, className = "" }) {
  const reduce = useReducedMotion();

  if (reduce) return <span className={`block ${className}`}>{children}</span>;

  // The viewport observer MUST sit on the outer (static) span, not the inner one.
  // The inner span is translated down by 108% of its own height, so observing it
  // means watching an element that has displaced itself out of view — the taller
  // the text, the further it moves, and two-line headings never intersect at all,
  // so they never reveal. Variants let the static wrapper drive the moving child.
  return (
    <motion.span
      className={`block overflow-hidden pb-[0.14em] -mb-[0.14em] ${className}`}
      initial="masked"
      whileInView="shown"
      viewport={{ once: true, margin: "0px 0px -8% 0px" }}
    >
      <motion.span
        className="block"
        variants={{ masked: { y: "108%" }, shown: { y: "0%" } }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

export default Reveal;
